/**
 * Product-wide earliest calendar day for My Timesheet fill (local YYYY-MM-DD).
 * Kept as the absolute floor; date of joining can only raise the bound.
 */
export const PRODUCT_TIMESHEET_FILL_MIN_DATE_STR = '2026-06-01';

const YYYY_MM_DD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Normalize API / cookie dates to local `YYYY-MM-DD`, or null if unusable. */
export function normalizeTimesheetDateStr(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const isoDay = trimmed.slice(0, 10);
  if (YYYY_MM_DD.test(isoDay)) {
    return isoDay;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Effective fill minimum: later of product floor and the user's date of joining.
 * Missing / invalid DOJ leaves the product floor unchanged.
 */
export function resolveTimesheetFillMinDateStr(
  dateOfJoining: string | null | undefined,
): string {
  const doj = normalizeTimesheetDateStr(dateOfJoining);
  if (!doj) return PRODUCT_TIMESHEET_FILL_MIN_DATE_STR;
  return doj > PRODUCT_TIMESHEET_FILL_MIN_DATE_STR ? doj : PRODUCT_TIMESHEET_FILL_MIN_DATE_STR;
}

/** Read `dateOfJoining` from the login `userDetails` cookie payload. */
export function readDateOfJoiningFromUserDetailsCookie(
  cookieRaw: string | null | undefined,
): string | null {
  if (!cookieRaw?.trim()) return null;
  try {
    const decoded = decodeURIComponent(cookieRaw);
    const user = JSON.parse(decoded) as {
      dateOfJoining?: string | null;
      user?: { dateOfJoining?: string | null };
    };
    return normalizeTimesheetDateStr(user?.dateOfJoining ?? user?.user?.dateOfJoining ?? null);
  } catch {
    return null;
  }
}
